import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { BannerMessage } from '../global';
import axiosHelper from '../helpers/axiosHelper';
import { set } from 'react-hook-form';

interface BannerContextType {
  bannerMessages: BannerMessage[];
  addBannerMessage: (message: BannerMessage) => void;
  dismissBannerMessage: (id: string) => void;
  leavePage: () => void;
  createAndAddBannerMessage: (title: string, category?: 'error' | 'success' | 'warning', id?: string, isCookieDismissable?: boolean) => void;
}

const BannerContext = createContext<BannerContextType | undefined>(undefined);

interface BannerProviderProps {
  children: ReactNode;
}

export const BannerProvider: React.FC<BannerProviderProps> = ({ children }) => {
  const [bannerMessages, setBannerMessages] = useState<BannerMessage[]>([]);
  const [nextMessages, setNextMessages] = useState<BannerMessage[]>([]);
  const addBannerMessage = (message: BannerMessage) => {
    // Check if the message already exists
    const exists = bannerMessages.some((msg) => msg.id === message.id);
    if (exists) {
      console.warn(`Banner message with id ${message.id} already exists.`);
      console.log(exists);
      return;
    }
    if (message.isCookieDismissable) {
      if (axiosHelper.getCookie('bnrdis' + message.id)) {
        console.log(`Banner message with id ${message.id} is already dismissed.`);
        return; // If the cookie exists, do not add the message
      }
    }
    setBannerMessages((prevMessages) => [...prevMessages, message]);
  };
  const leavePage = () => {
     setBannerMessages([]); 
  };
  const dismissBannerMessage = (id: string) => {
    const existingMessage = bannerMessages.find((msg) => msg.id === id);
    if (!existingMessage) {
      console.warn(`Banner message with id ${id} does not exist.`);
      return;
    }
    if (existingMessage.isCookieDismissable) {
      axiosHelper.setCookie('bnrdis' + id, 'true', 1);
    }
    console.log(`Removing banner message with id ${id}`);
    setBannerMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== id));
    // setNextMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== id));
  };
  const createAndAddBannerMessage = (title: string, category: 'error' | 'success' | 'warning' = 'success', id?: string, isCookieDismissable: boolean = false) => {
    const newMessage: BannerMessage = {
      id: id || new Date().getTime().toString(),
      title,
      category,
      isCookieDismissable: isCookieDismissable,
    };
    addBannerMessage(newMessage);
    if (isCookieDismissable) {
      // Set a cookie to remember the dismissal
      // axiosHelper.setCookie(newMessage.id, 'dismissed', 30); // Dismiss for 30 days
    }
  };
  return (
    <BannerContext.Provider
      value={{
        bannerMessages,
        addBannerMessage,
        dismissBannerMessage,
        leavePage,
        createAndAddBannerMessage,
      }}
    >
      {children}
    </BannerContext.Provider>
  );
};

export const useBanner = () => {
  const context = useContext(BannerContext);
  if (context === undefined) {
    throw new Error('useBanner must be used within an BannerProvider');
  }
  return context;
};
